// src/services/transactionFetcher/infinaeonTransactionFetcher.js
/* global BigInt */
import { TRANSACTION_TYPES } from '../../contexts/TransactionContext';
import { standardizeTransactionFormat } from './transactionFormatUtils';
import secureFetch from '../../utils/secureFetch';

/**
 * Safely determine if a transaction involves the wallet address
 * @param {string|Object} address - Address or object with hash property
 * @param {string} walletAddress - User's wallet address
 * @returns {boolean} - True if addresses match
 */
const addressMatches = (address, walletAddress) => {
  if (!address || !walletAddress) return false;
  
  const walletLower = walletAddress.toLowerCase();
  
  // If address is an object with hash property
  if (address && typeof address === 'object' && address.hash) {
    return address.hash.toLowerCase() === walletLower;
  }
  
  // If address is a string
  if (typeof address === 'string') {
    return address.toLowerCase() === walletLower;
  }
  
  return false;
};

/**
 * Detect transaction type based on method, value, and addresses
 * @param {Object} tx - Transaction data from the API
 * @param {string} walletAddress - Address of the user's wallet
 * @returns {string} - Transaction type from TRANSACTION_TYPES
 */
const detectTransactionType = (tx, walletAddress) => {
  try {
    console.log(`Detecting transaction type for tx ${tx?.hash || 'unknown'}`);
    
    if (!tx || !walletAddress) {
      console.log("Missing tx or wallet address, returning UNKNOWN");
      return TRANSACTION_TYPES.UNKNOWN;
    }
    
    // Determine if the transaction is from or to the wallet
    const fromIsWallet = addressMatches(tx.from, walletAddress);
    const toIsWallet = addressMatches(tx.to, walletAddress);
    
    console.log(`Address check: fromIsWallet=${fromIsWallet}, toIsWallet=${toIsWallet}`);
    
    // Get transaction value safely
    const value = tx.value || '0';
    
    // Check for approve method
    if ((tx.method === 'approve') || 
        (tx.raw_input && tx.raw_input.startsWith('0x095ea7b3'))) {
      console.log("Detected APPROVE transaction");
      return TRANSACTION_TYPES.APPROVE;
    }
    
    // Check for swap method
    if ((tx.method && tx.method.includes('swap')) || 
        (tx.raw_input && (
          tx.raw_input.startsWith('0x5f3bd1c8') || // SushiSwap v3 swap
          tx.raw_input.startsWith('0x38ed1739') || // swapExactTokensForTokens
          tx.raw_input.startsWith('0x7ff36ab5') || // swapExactETHForTokens
          tx.raw_input.startsWith('0x4a25d94a') || // swapTokensForExactETH
          tx.raw_input.startsWith('0x18cbafe5') || // swapExactTokensForETH
          tx.raw_input.startsWith('0xfb3bdb41')    // swapETHForExactTokens
        ))) {
      console.log("Detected SWAP transaction");
      return TRANSACTION_TYPES.SWAP;
    }
    
    // Send = from wallet, not to wallet, with value
    if (fromIsWallet && !toIsWallet && value !== '0') {
      console.log("Detected SEND transaction");
      return TRANSACTION_TYPES.SEND;
    }
    
    // Receive = not from wallet, to wallet, with value
    if (!fromIsWallet && toIsWallet && value !== '0') {
      console.log("Detected RECEIVE transaction");
      return TRANSACTION_TYPES.RECEIVE;
    }
    
    // Contract interaction
    if ((tx.method && tx.method !== 'transfer') || 
        (tx.raw_input && tx.raw_input !== '0x' && tx.raw_input.length > 10)) {
      console.log("Detected CONTRACT transaction");
      return TRANSACTION_TYPES.CONTRACT;
    }
    
    // Default case
    console.log("Detected UNKNOWN transaction");
    return TRANSACTION_TYPES.UNKNOWN;
  } catch (error) {
    console.error("Error in detectTransactionType:", error);
    return TRANSACTION_TYPES.UNKNOWN;
  }
};

/**
 * Extract address safely from address object or string
 * @param {Object|string} addressData - Address data
 * @returns {string} - Extracted address string
 */
const extractAddress = (addressData) => {
  if (!addressData) return '';
  
  if (typeof addressData === 'object' && addressData.hash) {
    return addressData.hash;
  }
  
  if (typeof addressData === 'string') {
    return addressData;
  }
  
  return '';
};

/**
 * Map Infinaeon API response item to the app's transaction format
 * @param {Object} tx - Transaction data from the API
 * @param {string} walletAddress - Address of the user's wallet
 * @param {Object} chainConfig - Chain configuration
 * @returns {Object} - Standardized transaction object
 */
const mapTransaction = (tx, walletAddress, chainConfig) => {
  try {
    // Debug
    console.log(`Mapping transaction: ${tx?.hash || 'unknown'}`);
    
    // Ensure we have required fields with sensible defaults
    if (!tx) {
      console.error("Received null transaction object");
      return null;
    }
    
    // Handle hash
    const hash = tx.hash || '';
    if (!hash) {
      console.error("Transaction missing hash");
      return null;
    }
    
    // Parse timestamp
    const timestamp = tx.timestamp || Date.now();
    let parsedTimestamp;
    
    if (typeof timestamp === 'string') {
      // Handle ISO timestamp format
      parsedTimestamp = new Date(timestamp).getTime();
    } else {
      // Handle numeric timestamp (could be in seconds)
      parsedTimestamp = typeof timestamp === 'number' && timestamp < 1000000000000 
        ? timestamp * 1000  // Convert from seconds to milliseconds
        : timestamp;
    }
    
    // Calculate fee (gasUsed * gasPrice) with safety checks
    const gasUsed = tx.gas_used || '0';
    const gasPrice = tx.gas_price || '0';
    let fee = '0';
    
    try {
      if (gasUsed && gasPrice) {
        fee = (BigInt(gasUsed) * BigInt(gasPrice)).toString();
      } else if (tx.fee && tx.fee.value) {
        fee = tx.fee.value.toString();
      }
    } catch (e) {
      console.error("Error calculating fee:", e);
    }
    
    // Extract from/to addresses safely
    const from = extractAddress(tx.from);
    const to = extractAddress(tx.to);
    
    // Detect transaction type using our safe function
    console.log("Calling detectTransactionType...");
    const type = detectTransactionType(tx, walletAddress);
    console.log(`Transaction type detected: ${type}`);
    
    // Get token and explorer info from chain config or use defaults
    const tokenSymbol = chainConfig?.nativeTokenSymbol || 'ETH';
    const tokenDecimal = chainConfig?.nativeTokenDecimals || 18;
    const network = chainConfig?.key || 'infinaeon';
    const explorerUrl = chainConfig?.explorerUrl 
      ? `${chainConfig.explorerUrl}/tx/${hash}` 
      : `https://explorer.infinaeon.com/tx/${hash}`;
    
    // Create transaction object
    return {
      hash,
      type,
      from,
      to,
      value: tx.value || '0',
      timestamp: parsedTimestamp,
      status: tx.status === 'ok' || tx.result === 'success' ? 'success' : 'failed',
      tokenSymbol,
      tokenDecimal,
      fee,
      network,
      data: tx.raw_input || '',
      methodName: tx.method || '',
      explorerUrl
    };
  } catch (error) {
    console.error("Error in mapTransaction:", error);
    return null;
  }
};

/**
 * Fetch transactions for an Infinaeon wallet using the Infinaeon Explorer API.
 * Transforms the response to match the expected format in TransactionContext.
 *
 * @param {string} walletAddress - The wallet address to query
 * @param {Object} options - Additional options like limit, nextToken, etc.
 * @param {Object} chainConfig - Chain configuration from supportedChains
 * @returns {Promise<{transactions: Array, hasMore: boolean, nextToken: string}>} - Transactions and pagination info
 */
export const fetchTransactions = async (walletAddress, options = {}, chainConfig = null) => {
  try {
    console.log("Starting Infinaeon transaction fetch...");
    const { limit = 50, nextToken = null } = options;
    
    // Ensure we have a valid wallet address
    if (!walletAddress || walletAddress.length < 40) {
      console.error("Invalid wallet address:", walletAddress);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    console.log(`Fetching Infinaeon transactions for address: ${walletAddress}`);
    
    // Base URL for Infinaeon Explorer API
    let url = `https://explorer.infinaeon.com/api/v2/addresses/${walletAddress}/transactions`;
    
    // Add pagination parameters if available
    if (nextToken) {
      let nextPageParams;
      try {
        if (typeof nextToken === 'string' && (nextToken.startsWith('{') || nextToken.startsWith('['))) {
          nextPageParams = JSON.parse(nextToken);
        } else {
          nextPageParams = nextToken;
        }
      } catch (e) {
        console.warn("Error parsing nextToken, using as is:", e);
        nextPageParams = nextToken;
      }
      
      url += `&${new URLSearchParams({
        next_page_params: JSON.stringify(nextPageParams)
      }).toString()}`;
    }
    
    console.log(`Infinaeon API URL: ${url}`);
    
    const response = await secureFetch(url);
    
    if (!response.ok) {
      console.error(`API error: ${response.status} - ${response.statusText}`);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    const data = await response.json();
    console.log(`API response received with ${data?.items?.length || 0} transactions`);
    
    // Check if the response has the expected structure
    if (!data || !Array.isArray(data.items)) {
      console.error("Unexpected API response format");
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    // Transform the data carefully with extra error handling
    console.log("Mapping transactions...");
    const mappedTransactions = [];
    
    for (const item of data.items) {
      try {
        const mappedTx = mapTransaction(item, walletAddress, chainConfig);
        if (mappedTx) {
          mappedTransactions.push(mappedTx);
        }
      } catch (err) {
        console.error(`Error mapping transaction ${item?.hash || 'unknown'}:`, err);
      }
    }
    
    console.log(`Successfully mapped ${mappedTransactions.length} transactions`);
    
    // Check if there are more transactions to load
    const hasMore = !!data.next_page_params;
    
    // Get the next page parameters
    const nextPageToken = data.next_page_params ? 
                          JSON.stringify(data.next_page_params) : 
                          null;
    
    // Return transactions along with pagination information
    console.log("Completing Infinaeon transaction fetch");
    return {
      transactions: standardizeTransactionFormat(mappedTransactions),
      hasMore,
      nextToken: nextPageToken
    };
  } catch (error) {
    console.error("Error fetching Infinaeon transactions:", error);
    return { transactions: [], hasMore: false, nextToken: null };
  }
};